import React, { useMemo, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import { Chip } from 'ui';
import { useSearchSkillTemplatesWithSearchTerm } from 'components/Skills/queries/skillTemplates';
import { REACT_QUERY_STATUS } from 'constants/config';
import { useFeatureFlags, useDebouncedValue } from 'hooks';
import { useGetReadinessSkills } from '../queries/readinessSkills';

const SEARCH_LIMIT = 20;

const EditAssociatedSkills = ({
  selectedSkills,
  setSelectedSkills,
  selectedSkillTags,
  setSelectedSkillTags,
}) => {
  const hasSkillsEnabled = useFeatureFlags('readiness-enable-skills-2-0');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm);

  const { data: suggestedSkillTemplates, status } =
    useSearchSkillTemplatesWithSearchTerm({
      searchTerm: debouncedSearchTerm,
      limit: SEARCH_LIMIT,
      offset: 0,
      hasNoCategory: false,
      mostRecent: false,
      excludedIds: selectedSkills.map(sk => sk.id),
      skip: hasSkillsEnabled,
    });

  const { data: suggestedSkillTags, status: searchSkillTagsStatus } =
    useGetReadinessSkills({
      limit: SEARCH_LIMIT,
      offset: 0,
      searchTerm: debouncedSearchTerm,
      sort: 'asc',
      onlyTags: true,
      excludedIds: selectedSkillTags.map(sk => sk.id),
      skip: !hasSkillsEnabled,
    });

  const suggestions = useMemo(() => {
    if (hasSkillsEnabled) {
      if (searchSkillTagsStatus === REACT_QUERY_STATUS.SUCCESS) {
        return suggestedSkillTags.readinessSkills.readinessSkills;
      }
      return [];
    }
    if (status === REACT_QUERY_STATUS.SUCCESS) {
      return suggestedSkillTemplates.skillTemplates.skillTemplates;
    }
    return [];
  }, [
    suggestedSkillTags,
    suggestedSkillTemplates,
    hasSkillsEnabled,
    searchSkillTagsStatus,
    status,
  ]);

  const onSuggestionsFetchRequested = value => {};
  const onSuggestionsClearRequested = () => {};
  const onChange = (_e, { newValue }) => {
    setSearchTerm(newValue);
  };
  const onSuggestionSelected = (_e, { suggestion }) => {
    setSearchTerm('');
    if (hasSkillsEnabled) {
      setSelectedSkillTags(prevSelected => [...prevSelected, suggestion]);
    } else {
      setSelectedSkills(prevSelected => [...prevSelected, suggestion]);
    }
  };

  return (
    <div className="EditAssociatedSkills">
      <Autosuggest
        suggestions={suggestions}
        shouldRenderSuggestions={() => suggestions.length !== 0}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        getSuggestionValue={suggestion => suggestion.name}
        renderSuggestion={suggestion => <span>{suggestion.name}</span>}
        inputProps={{
          placeholder: hasSkillsEnabled
            ? t('skills.general.search_skill_tags')
            : t('skills.general.search_skills'),
          value: searchTerm,
          onChange,
          type: 'search',
          disabled: false,
        }}
        theme={{
          container: 'EditAssociatedSkills-react-autosuggest__container',
          containerOpen:
            'EditAssociatedSkills-react-autosuggest__container--open',
          input: 'EditAssociatedSkills-react-autosuggest__input',
          inputOpen: 'EditAssociatedSkills-react-autosuggest__input--open',
          inputFocused:
            'EditAssociatedSkills-react-autosuggest__input--focused',
          suggestionsContainer: 'react-autosuggest__suggestions-container',
          suggestionsContainerOpen:
            'EditAssociatedSkills-react-autosuggest__suggestions-container--open',
          suggestionsList: 'react-autosuggest__suggestions-list',
          suggestion: 'EditAssociatedSkills-react-autosuggest__suggestion',
          suggestionFirst: 'react-autosuggest__suggestion--first',
          suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
          sectionContainer: 'react-autosuggest__section-container',
          sectionContainerFirst: 'react-autosuggest__section-container--first',
          sectionTitle: 'react-autosuggest__section-title',
        }}
      />
      <div className="fw-w d-f fd-r">
        {hasSkillsEnabled &&
          selectedSkillTags.map(skillTag => (
            <div key={skillTag.name} className="mt-8 mr-8">
              <Chip
                value={skillTag.name}
                ariaLabel={skillTag.name}
                onClick={() => {
                  setSelectedSkillTags(
                    selectedSkillTags.filter(sk => sk.id !== skillTag.id),
                  );
                }}
                variant="default"
                size="small"
              />
            </div>
          ))}
        {!hasSkillsEnabled &&
          selectedSkills.map(skill => (
            <div key={skill.name} className="mt-8 mr-8">
              <Chip
                value={skill.name}
                ariaLabel={skill.name}
                onClick={() => {
                  setSelectedSkills(
                    selectedSkills.filter(sk => sk.id !== skill.id),
                  );
                }}
                variant="default"
                size="small"
              />
            </div>
          ))}
      </div>
    </div>
  );
};

export default EditAssociatedSkills;
